import JSZip from "jszip";
import type { PIMProduct } from "../../../../../types/types.PIM";
import { rowsToCsv } from "../../../../../components/AgenticSearch/tableExport";
import {
  attributeValueToPlainText,
  findAttributeByName,
  findCollectionByName,
  findGroupByName,
  getAlignedCollectionColumns,
  getAlignedCollectionItems,
  getAlignedGroupAttributes,
  getCollectionAttributeForProduct,
  getCompareSections,
  type CompareSection,
} from "./CompareProducts.util";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function buildGroupCsv(
  products: PIMProduct[],
  section: CompareSection
): string {
  const attributes = getAlignedGroupAttributes(products, section.name);
  const header = [
    { text: "Attribute" },
    ...products.map((p) => ({ text: p.name || p.product_number || p.id })),
  ];
  const rows = attributes.map((attrDef) => {
    const cells = [
      { text: attrDef.display_name || attrDef.name },
      ...products.map((product) => {
        const group = findGroupByName(product, section.name);
        const attr = findAttributeByName(group, attrDef.name);
        return { text: attributeValueToPlainText(attr) };
      }),
    ];
    return cells;
  });
  return rowsToCsv([header, ...rows]);
}

function buildCollectionCsv(
  products: PIMProduct[],
  section: CompareSection
): string {
  const columns = getAlignedCollectionColumns(products, section.name);
  const items = getAlignedCollectionItems(products, section.name);
  const productHeaders = products.map(
    (p) => p.name || p.product_number || p.id
  );

  const header = [
    { text: "Item" },
    { text: "Attribute" },
    ...productHeaders.map((text) => ({ text })),
  ];

  const rows = items.flatMap((item) =>
    columns.map((colDef, colIndex) => {
      const cells = [
        { text: colIndex === 0 ? item.rowKey : "" },
        { text: colDef.display_name || colDef.name },
        ...products.map((product) => {
          const collection = findCollectionByName(product, section.name);
          const row = item.rowsByProductId[product.id];
          const attr = getCollectionAttributeForProduct(
            collection,
            row,
            colDef
          );
          return { text: attributeValueToPlainText(attr) };
        }),
      ];
      return cells;
    })
  );

  return rowsToCsv([header, ...rows]);
}

export async function exportCompareZip(
  products: PIMProduct[],
  selectedSections: CompareSection[]
): Promise<void> {
  const zip = new JSZip();
  const stamp = new Date().toISOString().slice(0, 10);

  for (const section of selectedSections) {
    const csv =
      section.kind === "group"
        ? buildGroupCsv(products, section)
        : buildCollectionCsv(products, section);
    zip.file(
      `${slugify(section.display_name || section.name)}.csv`,
      `\uFEFF${csv}`
    );
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `product_comparison_${stamp}.zip`;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export function getExportableSections(products: PIMProduct[]): CompareSection[] {
  if (!products.length) return [];
  return getCompareSections(products[0], products);
}
